import { CloseOutlined, InfoCircleOutlined, SaveOutlined } from '@ant-design/icons';
import { Button, Checkbox, Col, Form, Input, Row, Select, Space, Spin, Tooltip } from 'antd';
import { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router';
import { StatePieceStatus } from '../../../../../enums';
import { IAddBankAccountLookupListFormValues } from '../../../../../interfaces/lookup/bankAccount';
import { IAddBankAccount } from '../../../../../interfaces/lookup/bankAccount/data';
import { ContentHeader } from '../../../../../shared/components/appSkeleton/contentHeader/ContentHeader';
import { FetchResult } from '../../../../../shared/components/fetchResult/FetchResult';
import { ROW_GUTTER } from '../../../../../shared/Constants';
import { isError } from '../../../../../shared/helpers';
import { AppDispatch, RootState } from '../../../../../store';
import { addBankAccount, fetchCountriesLookupList } from '../../../../../store/lookupSlice/LookupActions';
import { resetState } from '../../../../../store/lookupSlice/LookupSlice';

export const BankAccountLookupListAdd = (props: any) => {
    const dispatch = useDispatch<AppDispatch>();
    const navigate = useNavigate();
    const [form] = Form.useForm();
    const lookupSlice = useSelector((state: RootState) => state.lookupSlice);
    const { countries } = useSelector((state: RootState) => state.lookupSlice);

    const onFinish = (values: IAddBankAccountLookupListFormValues) => {
        const body: IAddBankAccount = {
            ...(values as any),
        };

        dispatch(addBankAccount(body));
    };

    useEffect(() => {
        if (lookupSlice.detailsPage.bankAccount.createStatus === StatePieceStatus.Success) {
            navigate('/admin/lookup-lists/bank-account', { replace: true });
        }
    }, [lookupSlice.detailsPage.bankAccount.createStatus]);

    useEffect(() => {
        dispatch(fetchCountriesLookupList());

        return () => {
            dispatch(resetState());
        };
    }, []);

    return (
        <Form
            form={form}
            name="bankaccount-lookup-add"
            onFinish={onFinish}
            layout="vertical"
            onValuesChange={(changedValue: any, values) => {
                form.setFieldsValue(values);
            }}
        >
            <ContentHeader
                title="Manage bank account lookup list"
                breadcrumbs={[
                    {
                        breadcrumbName: 'Admin',
                        path: '/admin',
                    },
                    {
                        breadcrumbName: 'Manage lookup lists',
                        path: '/admin/lookup-lists',
                    },
                    {
                        breadcrumbName: 'Bank account',
                        path: '/admin/lookup-lists/bank-account',
                    },
                    {
                        breadcrumbName: 'Create',
                        path: `/admin/lookup-lists/bank-account/add`,
                    },
                ]}
            >
                {!isError(lookupSlice.detailsPage.bankAccount.fetchStatus) && (
                    <Form.Item>
                        <Space>
                            <Button
                                icon={<CloseOutlined />}
                                onClick={() => navigate(-1)}
                                disabled={
                                    lookupSlice.detailsPage.bankAccount.updateStatus === StatePieceStatus.IsFetching ||
                                    lookupSlice.detailsPage.bankAccount.fetchStatus === StatePieceStatus.IsFetching
                                }
                            >
                                Close
                            </Button>
                            <Button
                                htmlType="submit"
                                type="primary"
                                icon={<SaveOutlined />}
                                loading={
                                    lookupSlice.detailsPage.bankAccount.updateStatus === StatePieceStatus.IsFetching ||
                                    lookupSlice.detailsPage.bankAccount.fetchStatus === StatePieceStatus.IsFetching
                                }
                            >
                                Save and close
                            </Button>
                        </Space>
                    </Form.Item>
                )}
            </ContentHeader>

            <div className="page-content">
                <Spin spinning={lookupSlice.detailsPage.bankAccount.fetchStatus === StatePieceStatus.IsFetching}>
                    {isError(lookupSlice.detailsPage.bankAccount.fetchStatus) ? (
                        <FetchResult status={lookupSlice.detailsPage.bankAccount.fetchStatus} about={'Bank account'} />
                    ) : (
                        <>
                            <Row>
                                <Col span={24}>
                                    <Space>
                                        <label className="group-label">Bank account Information</label>
                                    </Space>
                                </Col>
                            </Row>
                            <Row gutter={ROW_GUTTER}>
                                <Col span={8}>
                                    <label className="property required">Name</label>
                                    <Form.Item name="name" rules={[{ required: true, message: "'Name' is required" }]}>
                                        <Input />
                                    </Form.Item>
                                </Col>
                                <Col span={8}>
                                    <label className="property required">BBAN</label>
                                    <Form.Item name="bban">
                                        <Input />
                                    </Form.Item>
                                </Col>
                                <Col span={8}>
                                    <label className="property required">IBAN</label>
                                    <Form.Item name="iban" rules={[{ required: true, message: "'IBAN' is required" }]}>
                                        <Input />
                                    </Form.Item>
                                </Col>
                                <Col span={8}>
                                    <label className="property required">BIC</label>
                                    <Form.Item name="bic" rules={[{ required: true, message: "'BIC' is required" }]}>
                                        <Input />
                                    </Form.Item>
                                </Col>
                                <Col span={8}>
                                    <label className="property">
                                        Currency code{' '}
                                        <Tooltip title="The currency in which this Synthon bank account is held. This is the account currency shown on the payment files, not the currency of the individual payment.">
                                            <InfoCircleOutlined />
                                        </Tooltip>
                                    </label>
                                    <Form.Item
                                        name="bankAccountCurrencyCode"
                                        getValueFromEvent={(e) => e.target.value?.toUpperCase()}
                                        rules={[
                                            {
                                                pattern: /^[A-Z]{3}$/,
                                                message: "'Currency code' must be exactly 3 uppercase letters (ISO 4217)",
                                            },
                                        ]}
                                    >
                                        <Input maxLength={3} />
                                    </Form.Item>
                                </Col>
                                <Col span={8}>
                                    <label className="property required">SEPA</label>
                                    <Form.Item name="sepa" valuePropName="checked">
                                        <Checkbox />
                                    </Form.Item>
                                </Col>
                                <Col span={8}>
                                    <label className="property required">Default</label>
                                    <Form.Item name="isDefault" valuePropName="checked">
                                        <Checkbox />
                                    </Form.Item>
                                </Col>
                            </Row>

                            {/* ADDRESS INFORMATION */}
                            <Row gutter={ROW_GUTTER}>
                                <Col span={24}>
                                    <Space>
                                        <label className="group-label">Address Information</label>
                                    </Space>
                                </Col>
                            </Row>
                            <Row gutter={ROW_GUTTER}>
                                <Col span={8}>
                                    <label className="property required">Address #1</label>
                                    <Form.Item name="addressline1" rules={[{ required: true, message: "'Address' is required" }]}>
                                        <Input />
                                    </Form.Item>
                                </Col>
                                <Col span={8}>
                                    <label className="property">Address #2</label>
                                    <Form.Item name="addressline2">
                                        <Input />
                                    </Form.Item>
                                </Col>
                                <Col span={8}>
                                    <label className="property required">Postal code</label>
                                    <Form.Item name="postalCode" rules={[{ required: true, message: "'Postal code' is required" }]}>
                                        <Input />
                                    </Form.Item>
                                </Col>
                                <Col span={8}>
                                    <label className="property required">City</label>
                                    <Form.Item name="city" rules={[{ required: true, message: "'City' is required" }]}>
                                        <Input />
                                    </Form.Item>
                                </Col>
                                <Col span={8}>
                                    <label className="property required">Country</label>
                                    <Form.Item name="countryId" rules={[{ required: true, message: "'Country' is required" }]}>
                                        <Select
                                            showSearch
                                            placeholder="Select a country"
                                            filterOption={(input, option) =>
                                                (option!.children as unknown as string).toLowerCase().startsWith(input.toLowerCase())
                                            }
                                        >
                                            {countries.lookup.data
                                                .filter((f) => !f.isDeleted)
                                                .map((country) => (
                                                    <Select.Option key={country.key} value={country.key} disabled={country.isDeleted}>
                                                        {country.description}
                                                    </Select.Option>
                                                ))}
                                        </Select>
                                    </Form.Item>
                                </Col>
                            </Row>
                        </>
                    )}
                </Spin>
            </div>
        </Form>
    );
};
